import hourlyCost from "@/data/trip-hourly-cost";
import { KTextField } from "kinnect-general";
import { formatCurrency, formatDuration } from "kinnect-formatters";
import { gmapApi } from "vue2-google-maps";

export default {
	name: "KEstimatedTripDetails",

	props: {
		startLatLng: {
			default: () => ( {
				lat: 36.115717,
				lng: -97.0498469
			} ),
			type: Object
		},
		latLngs: {
			default: () => [],
			type: Array
		},
		endLatLng: {
			default: () => ( {
				lat: 36.115717,
				lng: -97.0498469
			} ),
			type: Object
		},
		loadTimeSeconds: {
			default: 0,
			type: Number
		}
	},

	data: () => ( {
		cachedEstDetails: {},
		loading: false,
		cost: 0,
		duration: 0,
		distance: 0
	} ),

	computed: {
		google: gmapApi,

		cacheKey() {
			const locations = this.latLngs.slice( 0 );

			locations.push( this.startLatLng );
			locations.push( this.endLatLng );

			return locations
			.sort()
			.map( latLng => `${latLng.lat},${latLng.lng}` )
			.join( "|" );
		},

		estDetails() {
			if ( this.distance && this.duration && this.cost ) {
				const distance = `${this.convertMetersToMiles( this.distance )} miles`;
				const duration = formatDuration( this.duration * 1000, false, true );
				const cost = formatCurrency( this.cost );

				return `${distance}, ${duration}, ${cost}`;
			}

			return null;
		},

		loadTimeCost() {
			return ( this.loadTimeSeconds / 3600 ) * hourlyCost;
		},

		waypoints() {
			return this.latLngs.map( latLng => ( {
				location: latLng,
				stopover: true
			} ) );
		}
	},

	watch: {
		loading( val ) {
			this.$emit( "loading", val );
		},
		cost( val ) {
			this.$emit( "cost", val );
		},
		distance( val ) {
			this.$emit( "distance", val );
		},
		duration( val ) {
			this.$emit( "duration", val );
		},
		loadTimeSeconds() {
			this.updateDetails();
		},
		startLatLng() {
			this.updateDetails();
		},
		waypoints() {
			this.updateDetails();
		},
		endLatLng() {
			this.updateDetails();
		}
	},

	methods: {
		async updateDetails() {
			this.loading = true;

			try {
				await this.getEstDetails().then( details => {
					this.cost = parseFloat( ( details.cost + this.loadTimeCost ).toFixed( 2 ) );
					this.distance = details.distance;
					this.duration = details.duration + this.loadTimeSeconds;
				} );
			} catch ( e ) {
				this.cost = 0;
				this.distance = 0;
				this.duration = 0;
			} finally {
				this.loading = false;
			}
		},

		convertMetersToMiles( meters ) {
			return Math.round( meters / 1609.344 );
		},

		getEstDetails() {
			return new Promise( ( resolve, reject ) => {
				const initialCacheKey = this.cacheKey;
				const cachedEstDetails = this.cachedEstDetails[initialCacheKey];

				if ( cachedEstDetails ) {
					return resolve( cachedEstDetails );
				}

				if ( !this.waypoints.length ) {
					return reject();
				}

				this.$gmapApiPromiseLazy().then( () => {
					const {
						DirectionsService,
						DirectionsStatus,
						DirectionsTravelMode,
						UnitSystem
					} = this.google.maps;
					const directionsService = new DirectionsService();

					directionsService.route( {
						destination: this.endLatLng,
						optimizeWaypoints: true,
						origin: this.startLatLng,
						travelMode: DirectionsTravelMode.DRIVING,
						unitSystem: UnitSystem.IMPERIAL,
						waypoints: this.waypoints
					}, ( { routes }, status ) => {
						if ( status !== DirectionsStatus.OK ) {
							return reject();
						}

						const legs = Object.values( routes[0].legs );
						const tripDistance = legs.reduce( ( distance, leg ) => distance + leg.distance.value, 0 );
						const tripDuration = legs.reduce( ( duration, leg ) => duration + leg.duration.value, 0 );
						const tripCost = ( tripDuration / 3600 ) * hourlyCost;

						const result = {
							cost: tripCost,
							distance: tripDistance,
							duration: tripDuration
						};

						this.cachedEstDetails[initialCacheKey] = result;

						// Confirm cache key is the same as when the async call started
						if ( this.cacheKey === initialCacheKey ) {
							return resolve( result );
						}
					} );
				} );
			} );
		}
	},

	created() {
		this.updateDetails();
	},

	render() {
		const fieldData = {
			attrs: this.$attrs,
			directives: this.$vnode.data.directives,
			on: this.$listeners
		};

		return <KTextField
			disabled
			loading={this.loading}
			prependIcon="info"
			value={this.estDetails}
			{...fieldData}
		></KTextField>;
	}
};
